import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Loader2, Edit, Trash2, Users, Shield, Mail, Phone, UserCheck, UserX } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLES = [
  { value: 'admin', label: 'Administrator', description: 'Full system access' },
  { value: 'manager', label: 'Manager', description: 'Property and staff management' },
  { value: 'reservation_supervisor', label: 'Reservation Supervisor', description: 'Manage all reservations' },
  { value: 'front_desk', label: 'Front Desk', description: 'Create and view reservations' },
  { value: 'pool_attendant', label: 'Pool Attendant', description: 'Check-in and assist guests' },
  { value: 'beach_attendant', label: 'Beach Attendant', description: 'Check-in and assist guests' },
  { value: 'finance_readonly', label: 'Finance (Read-only)', description: 'View reports only' },
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'front_desk',
    phone: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');

      if (editingUser) {
        // Update existing user
        const updateData = {
          full_name: formData.full_name,
          role: formData.role,
          phone: formData.phone || null
        };
        await axios.put(`${API_URL}/users/${editingUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User updated successfully');
      } else {
        // Create new user
        if (!formData.password) {
          toast.error('Password is required for new users');
          return;
        }
        await axios.post(`${API_URL}/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User created successfully');
      }

      setDialogOpen(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      phone: user.phone || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deactivated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'front_desk',
      phone: ''
    });
  };

  const openNewDialog = () => {
    setEditingUser(null);
    resetForm();
    setDialogOpen(true);
  };

  const getRoleInfo = (role) => {
    return ROLES.find(r => r.value === role) || { label: role, description: '' };
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-purple-100 text-purple-800';
      case 'reservation_supervisor': return 'bg-blue-100 text-blue-800';
      case 'front_desk': return 'bg-green-100 text-green-800';
      case 'pool_attendant':
      case 'beach_attendant': return 'bg-cyan-100 text-cyan-800';
      case 'finance_readonly': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout title="User Management">
      <div className="mb-6 flex justify-between items-center">
        <p className="text-[#78716C]">Manage staff accounts and access permissions</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNewDialog}
              className="bg-[#9F1239] hover:bg-[#881337] text-white"
              data-testid="btn-add-user"
            >
              <Plus className="mr-2 h-4 w-4" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif">
                {editingUser ? 'Edit User' : 'Create New User'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Smith"
                  required
                  data-testid="input-full-name"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@resort.com"
                  required
                  disabled={!!editingUser}
                  data-testid="input-email"
                />
                {editingUser && (
                  <p className="text-xs text-[#78716C] mt-1">Email cannot be changed</p>
                )}
              </div>

              {!editingUser && (
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a secure password"
                    required={!editingUser}
                    data-testid="input-password"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  data-testid="input-phone"
                />
              </div>

              <div>
                <Label htmlFor="role">Role *</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full p-2 border border-[#E7E5E4] rounded-sm"
                  required
                  data-testid="select-role"
                >
                  {ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-[#78716C] mt-1">
                  {getRoleInfo(formData.role).description}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#9F1239] hover:bg-[#881337]"
                data-testid="btn-submit"
              >
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#9F1239]" />
        </div>
      ) : (
        <div className="space-y-4">
          {users.length === 0 ? (
            <Card className="bg-white border-slate-200 shadow-sm rounded-md">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#78716C]">No users found.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {users.map((user) => (
                <Card 
                  key={user.id} 
                  className={`bg-white border-slate-200 shadow-sm rounded-md ${!user.active ? 'opacity-60' : ''}`}
                  data-testid={`user-${user.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          user.active ? 'bg-[#9F1239]' : 'bg-gray-400'
                        }`}>
                          <span className="text-white font-bold text-lg">
                            {user.full_name?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-serif font-medium text-[#1C1917]">
                              {user.full_name}
                            </h3>
                            {!user.active && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded flex items-center gap-1">
                                <UserX className="w-3 h-3" /> Inactive
                              </span>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-[#78716C]">
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {user.email}
                            </span>
                            {user.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                {user.phone}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <span className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${getRoleBadgeColor(user.role)}`}>
                              <Shield className="w-3 h-3" />
                              {getRoleInfo(user.role).label}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(user)}
                          data-testid={`btn-edit-${user.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {user.active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`btn-delete-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Role Permissions Info */}
      <Card className="bg-[#FAFAF9] border-[#E7E5E4] shadow-sm rounded-md mt-8">
        <CardContent className="p-6">
          <h4 className="font-serif font-medium text-[#1C1917] mb-4">Role Permissions</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ROLES.map(role => (
              <div key={role.value} className="p-3 bg-white rounded-sm border border-[#E7E5E4]">
                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mb-2 ${getRoleBadgeColor(role.value)}`}>
                  {role.label}
                </span>
                <p className="text-sm text-[#78716C]">{role.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
